import { Button, Input, Portal } from '@common/components';
import {
  SvgChevronleft,
  SvgDelete,
  SvgExpandWindow,
  SvgHome,
  SvgKebab,
  SvgLogo,
  SvgMagnifyingGlass,
  SvgNotification,
  SvgSetting,
} from '@common/icon';
import { cn } from '@core/utils';
import { usePathname } from 'next/navigation';
import { type ReactNode, useEffect } from 'react';

import { CustomLink } from '@/app/_components/CustomLink';
import { Image } from '@/components/client';
import { GLOBAL_LAYOUT_PAGE_NAV_WRAPPER_CLASS } from '@/components/template/GlobalLayout/constants';
import {
  DONATION_ROUTES,
  MODULE_ROUTES,
  ROUTES,
  SELECTOR,
  SUB_DOMAIN,
} from '@/constants';
import { useCustomRouter } from '@/hooks/useCustomRouter';
import { TossExitConfirmModal, useTossWebView } from '@/modules/toss';
import type { FlutterInAppWebViewWindow } from '@/modules/wello-app';
import { useDeviceInfoStore, WEBVIEW_TYPE } from '@/stores/DeviceStore';
import { useGlobalStore } from '@/stores/GlobalStore';
import { findRoute } from '@/utils';
import { gtmDatasets } from '@/utils/analytics/gtm';

const TOSS_LOGO_URL = 'https://cdn.welfarehello.com/static/toss-logo.png';

type NavigationRouter = Pick<
  ReturnType<typeof useCustomRouter>,
  'push' | 'replace' | 'back'
>;

export type PageNavigationContentProps = Omit<
  PageNavigationProps,
  'placeholder'
> & {
  isToss: boolean;
  isKbWebview: boolean;
  pathname: string;
  setShareLink?: (link: string) => void;
  router?: NavigationRouter;
  openConfirmModal?: VoidFunction;
  closeConfirmModal?: VoidFunction;
  confirmAndClose?: VoidFunction;
  isModalOpened?: boolean;
};

export const PageNavigationContent = ({
  logo,
  logoHref,
  title,
  titleLeft,
  children,
  backButton = true,
  backButtonType = 'back',
  searchButton,
  bellButton,
  notification,
  shareButton,
  settingsButton,
  kebabButton,
  onClickBack,
  onClickKebab,
  hideHomeButton,
  className,
  style,
  textColor,
  isNotUsedHomeButton,
  donationHomeButton,
  backgroundColor,
  searchForm,
  searchFormDefaultValue,
  searchFormClassName,
  onSearchFormMount,
  onSearchFormClick,
  onSearchFormSubmit,
  isToss,
  pathname,
  setShareLink,
  router,
  openConfirmModal,
  closeConfirmModal,
  confirmAndClose,
  isModalOpened,
}: PageNavigationContentProps) => {
  const hasBottomNav = findRoute(pathname)?.hasBottomNav;
  const safeRouter = {
    push: router?.push ?? (() => {}),
    replace: router?.replace ?? (() => {}),
    back: router?.back ?? (() => {}),
  };
  const handleShareLink = (link: string) => setShareLink?.(link);
  const handleOpenConfirm = () => openConfirmModal?.();
  const handleCloseConfirm = () => closeConfirmModal?.();
  const handleConfirmAndClose = () => confirmAndClose?.();
  const modalOpened = Boolean(isModalOpened);
  const DEFAULT_LOGO_HREF = isToss
    ? DONATION_ROUTES.HOME.pathname
    : ROUTES.HOME.pathname;

  return (
    <nav
      aria-label="페이지 내비게이션"
      className={cn(
        'relative flex min-h-48 w-full items-center',
        'box-border bg-Gray-white text-center',
        className,
      )}
      style={{
        backgroundColor: backgroundColor === 'gray' ? '#f3f3f3' : undefined,
        ...style,
      }}
    >
      {titleLeft ? (
        <h3
          className={cn(
            'absolute left-20 font-700 flex-center typo-h6',
            textColor,
          )}
        >
          {titleLeft}
        </h3>
      ) : null}
      {title ? (
        <h3
          className={cn(
            'absolute flex size-full items-center justify-center',
            'break-all text-center font-600 text-Gray-900 typo-body3',
            textColor,
          )}
        >
          {title}
        </h3>
      ) : null}
      <div className="z-[1] flex grow items-center gap-20 pl-20">
        {logo ? (
          <div className="flex items-center gap-12">
            <div
              className={cn('flex items-center gap-12', {
                'gap-2': isToss,
              })}
            >
              {isToss ? (
                <>
                  <Image
                    src={TOSS_LOGO_URL}
                    alt="toss"
                    width={31}
                    height={31}
                  />
                  <SvgDelete className="size-20 text-Gray-400" />
                </>
              ) : null}
              <CustomLink
                aria-label="웰로"
                href={logoHref ?? DEFAULT_LOGO_HREF}
                {...gtmDatasets.clickGnb({ menu_name: '로고' })}
              >
                <SvgLogo className="h-29 w-86" />
              </CustomLink>
            </div>
          </div>
        ) : null}
        {backButton ? (
          <Button
            aria-label="뒤로 가기"
            className="relative size-26 p-0"
            dimensions="H26"
            theme="primary-text"
            onClick={
              onClickBack
                ? typeof onClickBack === 'string'
                  ? () => safeRouter.replace(onClickBack)
                  : onClickBack
                : () => safeRouter.back()
            }
          >
            {backButtonType === 'back' ? (
              <SvgChevronleft className={cn('size-24', textColor)} />
            ) : (
              <SvgDelete className={cn('size-24', textColor)} />
            )}
          </Button>
        ) : null}
        {!hasBottomNav && !isNotUsedHomeButton ? (
          <Button
            aria-label="홈으로 가기"
            className={cn('relative size-26 p-0', hideHomeButton && 'hidden')}
            dimensions="H26"
            theme="primary-text"
            onClick={() => safeRouter.push(ROUTES.HOME.pathname)}
          >
            <SvgHome className={cn('size-24', textColor)} />
          </Button>
        ) : null}
        {donationHomeButton ? (
          <Button
            aria-label="고향사랑기부 홈으로 가기"
            className="relative size-26 p-0"
            dimensions="H26"
            theme="primary-text"
            onClick={() => safeRouter.push(DONATION_ROUTES.HOME.pathname)}
          >
            <SvgHome className={cn('size-24', textColor)} />
          </Button>
        ) : null}
        {searchForm ? (
          <Input
            ref={(el) => {
              if (!el) return;

              onSearchFormMount?.(el);
            }}
            key={searchFormDefaultValue}
            type="search"
            placeholder="검색어를 입력해 주세요"
            dimensions="H40"
            defaultValue={searchFormDefaultValue}
            className={cn(
              'my-12 w-full border-none bg-Gray-200 text-Gray-900 placeholder:text-Gray-550 focus:border-none',
              searchFormClassName,
            )}
            onClick={() => onSearchFormClick?.()}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                onSearchFormSubmit?.(e.currentTarget.value);
              }
            }}
            onClear={() => onSearchFormSubmit?.('')}
          />
        ) : null}
      </div>
      <div className="z-[1] ml-auto flex justify-end gap-20 pr-20">
        {children}

        {shareButton ? (
          <Button
            aria-label="공유하기"
            className="relative size-26 p-0"
            dimensions="H26"
            theme="primary-text"
            onClick={() => {
              handleShareLink(location.href);
            }}
          >
            <SvgExpandWindow className={cn('size-24', textColor)} />
          </Button>
        ) : null}
        {settingsButton ? (
          <Button
            aria-label="설정"
            className="relative size-26 p-0"
            dimensions="H26"
            theme="primary-text"
            onClick={() => safeRouter.push(ROUTES.MY_SETTINGS.pathname)}
          >
            <SvgSetting className={cn('size-24', textColor)} />
          </Button>
        ) : null}
        {searchButton ? (
          <Button
            aria-label="검색으로 이동"
            className="relative size-26 p-0"
            dimensions="H26"
            theme="primary-text"
            asChild
            {...gtmDatasets.clickGnb({ menu_name: '검색' })}
          >
            <CustomLink
              href={
                typeof searchButton === 'boolean'
                  ? ROUTES.SEARCH.pathname
                  : searchButton
              }
            >
              <SvgMagnifyingGlass className={cn('size-24', textColor)} />
            </CustomLink>
          </Button>
        ) : null}
        {bellButton ? (
          <Button
            aria-label="알림으로 이동"
            className="relative size-26 p-0"
            dimensions="H26"
            theme="primary-text"
            onClick={() => {
              safeRouter.push(ROUTES.PUSH.pathname);
            }}
          >
            <SvgNotification className={cn('size-24', textColor)} />
            {notification ? (
              <span className="absolute right-14 top-12 box-content size-4 rounded-pill border-2 border-Gray-100 bg-R-500" />
            ) : null}
          </Button>
        ) : null}
        {kebabButton ? (
          typeof kebabButton === 'boolean' ? (
            <Button
              aria-label="더보기"
              className="relative size-26 p-0"
              dimensions="H26"
              theme="primary-text"
              onClick={onClickKebab}
            >
              <SvgKebab className={cn('size-24', textColor)} />
            </Button>
          ) : (
            kebabButton
          )
        ) : null}
        {isToss ? (
          <>
            <Button
              aria-label="토스로 이동"
              className="relative h-26 p-0"
              dimensions="H40"
              theme="primary-text"
              onClick={handleOpenConfirm}
            >
              닫기
            </Button>
            <TossExitConfirmModal
              opened={modalOpened}
              onClose={handleCloseConfirm}
              onConfirm={handleConfirmAndClose}
            />
          </>
        ) : null}
      </div>
    </nav>
  );
};

export interface PageNavigationProps {
  className?: string;
  title?: string | ReactNode;
  titleLeft?: string | ReactNode;
  children?: ReactNode;
  logo?: boolean;
  logoHref?: string;
  searchButton?: boolean | string;
  bellButton?: boolean;
  shareButton?: boolean;
  notification?: boolean;
  settingsButton?: boolean;
  backButton?: boolean;
  backButtonType?: 'exit' | 'back';
  /** backButton 유무와 관계없이 뒤로가기 활성화 */
  enableHistoryBack?: boolean;
  kebabButton?: boolean | ReactNode;
  onClickKebab?: VoidFunction;
  hideHomeButton?: boolean;
  onClickBack?: VoidFunction | string;
  style?: React.CSSProperties;
  textColor?: string;
  isNotUsedHomeButton?: boolean;
  donationHomeButton?: boolean;
  /** logo가 true일 때 렌더링 되는 video backgroundColor에 영향을 줌 */
  backgroundColor?: 'gray';
  //* 검색 폼 관련
  searchForm?: boolean;
  searchFormDefaultValue?: string;
  searchFormClassName?: string;
  onSearchFormMount?: (ref: HTMLInputElement) => void;
  onSearchFormClick?: () => void;
  onSearchFormSubmit?: (value: string) => void;
  placeholder?: ReactNode;
}

export const PageNavigation = (props: PageNavigationProps) => {
  const {
    logo,
    logoHref,
    title,
    titleLeft,
    children,
    backButton = true,
    backButtonType = 'back',
    enableHistoryBack,
    searchButton,
    bellButton,
    notification,
    shareButton,
    settingsButton,
    kebabButton,
    onClickBack,
    onClickKebab,
    hideHomeButton,
    className,
    style,
    textColor,
    isNotUsedHomeButton,
    donationHomeButton,
    backgroundColor,
    searchForm,
    searchFormDefaultValue,
    searchFormClassName,
    onSearchFormMount,
    onSearchFormClick,
    onSearchFormSubmit,
  } = props;

  const router = useCustomRouter();
  const {
    openConfirmModal,
    closeConfirmModal,
    confirmAndClose,
    isModalOpened,
  } = useTossWebView();
  const setShareLink = useGlobalStore((state) => state.setShareLink);
  const [isKbWebview, setAppModal, isToss] = useDeviceInfoStore((state) => [
    state.webviewType === WEBVIEW_TYPE.KB_PAY,
    state.setAppModal,
    state.subDomain === SUB_DOMAIN.TOSS,
  ]);

  useEffect(() => {
    const _window = window as FlutterInAppWebViewWindow;

    if (_window.router) {
      _window.router.back =
        backButton || enableHistoryBack
          ? () => router.back()
          : () => {
              setAppModal({ isOpened: true, type: 'exit' });
            };

      return () => {
        _window.router.back = () => {};
      };
    }
  }, [backButton, enableHistoryBack, router, setAppModal]);

  const pathname = usePathname() ?? '';

  if (isKbWebview && pathname !== MODULE_ROUTES.KB_HOME.pathname) return null;

  const contentProps: PageNavigationContentProps = {
    logo,
    logoHref,
    title,
    titleLeft,
    children,
    backButton,
    backButtonType,
    enableHistoryBack,
    searchButton,
    bellButton,
    notification,
    shareButton,
    settingsButton,
    kebabButton,
    onClickBack,
    onClickKebab,
    hideHomeButton,
    className,
    style,
    textColor,
    isNotUsedHomeButton,
    donationHomeButton,
    backgroundColor,
    searchForm,
    searchFormDefaultValue,
    searchFormClassName,
    onSearchFormMount,
    onSearchFormClick,
    onSearchFormSubmit,
    isToss,
    isKbWebview,
    pathname,
    setShareLink,
    router,
    openConfirmModal,
    closeConfirmModal,
    confirmAndClose,
    isModalOpened,
  };

  return (
    <Portal
      //* placeholder 가 true일 때는 children 먼저 렌더링함.
      placeholder={true}
      renderTo={() => document.getElementById(SELECTOR.PAGE_NAV_WRAPPER)}
    >
      <PageNavigationContent
        {...contentProps}
        //* desktop 환경일 때는 페이지 네비게이션 렌더링 안하는 로직이 scss에 있음
        className={cn(GLOBAL_LAYOUT_PAGE_NAV_WRAPPER_CLASS, className)}
      />
    </Portal>
  );
};
